<template>
  <div class="mb-4 lead-orc-card-wrap">
    <div
      class="flex items-center justify-between mb-3"
      :class="{ 'border-b border-dashed border-gray-300 dark:border-gray-500 pb-3': minimized }"
    >
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center shadow-md">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('orcamentos.leadCard.title') }}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('orcamentos.leadCard.subtitle') }}</p>
        </div>
      </div>
      <button
        type="button"
        class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        @click="minimized = !minimized"
      >
        <svg
          class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform"
          :class="{ 'rotate-180': minimized }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
    </div>

    <div
      v-show="!minimized"
      class="lead-orc-card-surface modal-surface bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
    >
      <button type="button" class="lead-orc-card-create" @click="criar">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        {{ t('orcamentos.leadCard.create') }}
      </button>

      <div v-if="loading" class="lead-orc-card-loading text-sm text-gray-500 dark:text-gray-400">
        {{ t('orcamentos.loading') }}
      </div>

      <div v-else-if="!visibleItems.length" class="lead-orc-card-empty text-sm text-gray-500 dark:text-gray-400">
        {{ t('orcamentos.leadCard.empty') }}
      </div>

      <div v-else class="lead-orc-card-list space-y-3">
        <article
          v-for="orc in visibleItems"
          :key="orc.id"
          class="lead-orc-item modal-item-surface"
        >
          <button
            type="button"
            class="lead-orc-item__archive"
            :title="t('orcamentos.actions.archive')"
            :aria-label="t('orcamentos.actions.archive')"
            :disabled="archivingId === orc.id"
            @click="arquivarOrcamento(orc)"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
            </svg>
          </button>

          <div class="lead-orc-item__head">
            <div class="lead-orc-item__doc-icon" aria-hidden="true">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div class="lead-orc-item__meta min-w-0">
              <div class="lead-orc-item__title-row">
                <span class="lead-orc-item__numero text-indigo-600 dark:text-indigo-300">{{ orc.numero }}</span>
                <span
                  class="orc-status"
                  :class="orc.status === 'PUBLICADO' ? 'orc-status--publicado' : 'orc-status--rascunho'"
                >
                  {{ statusLabel(orc.status) }}
                </span>
              </div>
              <p class="lead-orc-item__titulo text-gray-900 dark:text-gray-100">
                {{ orc.titulo || t('orcamentos.list.untitled') }}
              </p>
              <p
                v-if="orc.dataCriacao"
                class="lead-orc-item__data text-gray-500 dark:text-gray-400"
                :title="formatOrcamentoDateTime(orc.dataCriacao)"
              >
                <svg class="lead-orc-item__data-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                {{ t('orcamentos.leadCard.generatedAt', { date: formatOrcamentoDate(orc.dataCriacao) }) }}
              </p>
              <p class="lead-orc-item__valor text-emerald-600 dark:text-emerald-400">{{ formatMoney(orc.valorTotal) }}</p>
            </div>
          </div>

          <div class="lead-orc-item__actions">
            <button
              type="button"
              class="lead-orc-action lead-orc-action--primary"
              :title="t('orcamentos.actions.edit')"
              :aria-label="t('orcamentos.actions.edit')"
              @click="editar(orc.id)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              <span class="lead-orc-action__label">{{ t('orcamentos.actions.edit') }}</span>
            </button>

            <button
              v-if="orc.publicUrl"
              type="button"
              class="lead-orc-action"
              :class="{ 'lead-orc-action--done': copiedId === orc.id }"
              :title="copyActionLabel(orc)"
              :aria-label="copyActionLabel(orc)"
              @click="copiar(orc)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              <span class="lead-orc-action__label">{{ copyActionLabel(orc) }}</span>
            </button>

            <button
              v-if="orc.publicUrl"
              type="button"
              class="lead-orc-action"
              :title="t('orcamentos.public.downloadPdf')"
              :aria-label="t('orcamentos.public.downloadPdf')"
              :disabled="pdfLoadingId === orc.id"
              @click="baixarPdf(orc)"
            >
              <svg v-if="pdfLoadingId !== orc.id" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span v-else class="lead-orc-action__spinner" aria-hidden="true" />
              <span v-if="pdfLoadingId !== orc.id" class="lead-orc-action__label">{{ t('orcamentos.public.downloadPdf') }}</span>
            </button>

            <button
              v-if="orc.publicUrl && canSendWhatsapp"
              type="button"
              class="lead-orc-action lead-orc-action--whatsapp"
              :title="t('orcamentos.actions.sendWhatsapp')"
              :aria-label="t('orcamentos.actions.sendWhatsapp')"
              :disabled="waSendingId === orc.id"
              @click="enviarWhatsapp(orc)"
            >
              <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span class="lead-orc-action__label">{{ t('orcamentos.actions.sendWhatsapp') }}</span>
            </button>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Swal from 'sweetalert2'
import api from '@/services/api'
import { formatOrcamentoMoney } from '@/utils/orcamentoUtils'
import { formatDate } from '@/utils/format'
import { exportOrcamentoPdfFromHtml } from '@/utils/orcamentoPdf'
import '@/assets/styles/workspace-orcamentos.css'

const props = defineProps({
  leadId: { type: Number, required: true },
  workspaceId: { type: Number, required: true },
  whatsappConnected: { type: Boolean, default: false },
  sendOrcamentoWhatsapp: { type: Function, default: null }
})

const router = useRouter()
const { t, locale } = useI18n()
const minimized = ref(false)
const loading = ref(false)
const items = ref([])
const copiedId = ref(null)
const pdfLoadingId = ref(null)
const waSendingId = ref(null)
const archivingId = ref(null)

let copiedTimer = null

const formatMoney = formatOrcamentoMoney

const visibleItems = computed(() => items.value.filter(orc => !orc.arquivado))

function formatOrcamentoDate (value) {
  return formatDate(value, locale.value) || '—'
}

function formatOrcamentoDateTime (value) {
  if (!value) return ''
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return String(value)
    return d.toLocaleString(locale.value)
  } catch {
    return String(value)
  }
}

const canSendWhatsapp = computed(() => props.whatsappConnected && typeof props.sendOrcamentoWhatsapp === 'function')

function statusLabel (status) {
  if (status === 'PUBLICADO') return t('orcamentos.status.publicado')
  return t('orcamentos.status.rascunho')
}

function copyActionLabel (orc) {
  return copiedId.value === orc.id ? t('orcamentos.list.copied') : t('orcamentos.actions.copyLink')
}

async function load () {
  if (!props.leadId) return
  loading.value = true
  try {
    const { data } = await api.get(`/leads/${props.leadId}/orcamentos`)
    items.value = Array.isArray(data) ? data : []
  } finally {
    loading.value = false
  }
}

function criar () {
  router.push(`/workspace/${props.workspaceId}/orcamentos/novo?leadId=${props.leadId}`)
}

function editar (id) {
  router.push(`/workspace/${props.workspaceId}/orcamentos/${id}?leadId=${props.leadId}`)
}

async function copiar (orc) {
  if (!orc.publicUrl) return
  try {
    await navigator.clipboard.writeText(orc.publicUrl)
    copiedId.value = orc.id
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => { copiedId.value = null }, 2200)
  } catch { /* ignore */ }
}

async function baixarPdf (orc) {
  const html = orc.snapshotHtml
  if (!html || pdfLoadingId.value) return
  pdfLoadingId.value = orc.id
  try {
    await exportOrcamentoPdfFromHtml(html, `${orc.numero || 'orcamento'}.pdf`)
  } finally {
    pdfLoadingId.value = null
  }
}

async function enviarWhatsapp (orc) {
  if (!orc.publicUrl || !canSendWhatsapp.value) return
  waSendingId.value = orc.id
  try {
    await props.sendOrcamentoWhatsapp(orc)
  } finally {
    waSendingId.value = null
  }
}

async function arquivarOrcamento (orc) {
  if (!orc?.id || !props.workspaceId || archivingId.value) return

  const result = await Swal.fire({
    title: t('orcamentos.archiveTitle'),
    html: `
      <p style="margin-bottom: 12px;">${t('orcamentos.archiveConfirm')}</p>
      <p style="color: #64748b; font-size: 14px;">${t('orcamentos.leadCard.archiveHint')}</p>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('orcamentos.confirmArchive'),
    cancelButtonText: t('common.cancel'),
    reverseButtons: true
  })

  if (!result.isConfirmed) return

  archivingId.value = orc.id
  try {
    await api.put(`/orcamentos/${orc.id}/arquivar`, null, {
      params: { workspaceId: props.workspaceId }
    })
    await load()
  } catch (e) {
    console.error(e)
    await Swal.fire({
      title: t('orcamentos.archiveErrorTitle'),
      text: t('orcamentos.archiveErrorText'),
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: '#ef4444'
    })
  } finally {
    archivingId.value = null
  }
}

watch(() => props.leadId, load)
onMounted(load)
</script>

<style scoped>
.lead-orc-card-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  margin-bottom: 14px;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.lead-orc-card-create:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35);
}

.lead-orc-card-loading,
.lead-orc-card-empty {
  padding: 12px 4px;
  text-align: center;
}

.lead-orc-item {
  position: relative;
  padding: 14px 40px 14px 14px;
  border-radius: 10px;
  border: 1px solid rgb(229 231 235);
  background: linear-gradient(180deg, rgb(249 250 251) 0%, rgb(255 255 255) 100%);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.lead-orc-item__archive {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgb(156 163 175);
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;
}

:global(.dark) .lead-orc-item__archive,
:global(body.tema_escura) .lead-orc-item__archive {
  color: rgb(156 163 175);
}

.lead-orc-item__archive:hover:not(:disabled) {
  opacity: 1;
  color: rgb(217 119 6);
  background: rgb(255 251 235);
}

:global(.dark) .lead-orc-item__archive:hover:not(:disabled),
:global(body.tema_escura) .lead-orc-item__archive:hover:not(:disabled) {
  color: rgb(251 191 36);
  background: rgb(120 53 15 / 0.35);
}

.lead-orc-item__archive:disabled {
  opacity: 0.35;
  cursor: wait;
}

:global(.dark) .lead-orc-item,
:global(body.tema_escura) .lead-orc-item {
  border-color: rgb(75 85 99);
  background: linear-gradient(180deg, rgb(31 41 55) 0%, rgb(17 24 39) 100%);
}

.lead-orc-item:hover {
  border-color: rgb(199 210 254);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.08);
}

:global(.dark) .lead-orc-item:hover,
:global(body.tema_escura) .lead-orc-item:hover {
  border-color: rgb(99 102 241 / 0.45);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
}

.lead-orc-item__head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.lead-orc-item__doc-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.lead-orc-item__meta {
  flex: 1;
  min-width: 0;
}

.lead-orc-item__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.lead-orc-item__numero {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: rgb(79 70 229);
}

:global(.dark) .lead-orc-item__numero,
:global(body.tema_escura) .lead-orc-item__numero {
  color: rgb(165 180 252);
}

.lead-orc-item__titulo {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: rgb(17 24 39);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-orc-item__data {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
}

.lead-orc-item__data-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  opacity: 0.85;
}

:global(.dark) .lead-orc-item__titulo,
:global(body.tema_escura) .lead-orc-item__titulo {
  color: rgb(243 244 246);
}

.lead-orc-item__valor {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: rgb(5 150 105);
}

:global(.dark) .lead-orc-item__valor,
:global(body.tema_escura) .lead-orc-item__valor {
  color: rgb(52 211 153);
}

.lead-orc-item__actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed rgb(229 231 235);
  overflow: hidden;
  container-type: inline-size;
}

@container (max-width: 430px) {
  .lead-orc-action__label {
    display: none;
  }

  .lead-orc-action {
    padding: 6px;
    gap: 0;
    min-width: 30px;
    justify-content: center;
  }
}

:global(.dark) .lead-orc-item__actions,
:global(body.tema_escura) .lead-orc-item__actions {
  border-top-color: rgb(75 85 99);
}

.lead-orc-action {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 5px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgb(229 231 235);
  background: rgb(255 255 255);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: rgb(55 65 81);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

:global(.dark) .lead-orc-action,
:global(body.tema_escura) .lead-orc-action {
  border-color: rgb(75 85 99);
  background: rgb(31 41 55);
  color: rgb(209 213 219);
}

.lead-orc-action:hover:not(:disabled) {
  border-color: rgb(199 210 254);
  background: rgb(238 242 255);
  color: rgb(67 56 202);
}

:global(.dark) .lead-orc-action:hover:not(:disabled),
:global(body.tema_escura) .lead-orc-action:hover:not(:disabled) {
  border-color: rgb(99 102 241 / 0.5);
  background: rgb(49 46 129 / 0.35);
  color: rgb(199 210 254);
}

.lead-orc-action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.lead-orc-action--primary {
  border-color: transparent;
  color: #fff;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
}

.lead-orc-action--primary:hover:not(:disabled) {
  color: #fff;
  background: linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%);
  border-color: transparent;
}

.lead-orc-action--whatsapp {
  border-color: transparent;
  color: #fff;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.lead-orc-action--whatsapp:hover:not(:disabled) {
  color: #fff;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  border-color: transparent;
}

.lead-orc-action--done {
  border-color: rgb(167 243 208);
  background: rgb(236 253 245);
  color: rgb(5 150 105);
}

:global(.dark) .lead-orc-action--done,
:global(body.tema_escura) .lead-orc-action--done {
  border-color: rgb(6 95 70);
  background: rgb(6 78 59 / 0.4);
  color: rgb(110 231 183);
}

.lead-orc-action__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgb(209 213 219);
  border-top-color: rgb(99 102 241);
  border-radius: 50%;
  animation: lead-orc-spin 0.7s linear infinite;
}

@keyframes lead-orc-spin {
  to { transform: rotate(360deg); }
}

:global(body.tema_clara) .lead-orc-item .orc-status--rascunho {
  background: color-mix(in srgb, #64748b 14%, #fff);
}

:global(body.tema_clara) .lead-orc-item .orc-status--publicado {
  background: color-mix(in srgb, #16a34a 14%, #fff);
}
</style>
